import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    CircularProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import authService from '../api-authorization/AuthorizeService';
import { useToast } from '../../hooks/useToast';
import { ToastNotification } from '../common/ToastNotification';
import { formatApiError } from '../../utils/errorFormatter';

interface IEmailSettings {
    id?: number;
    smtpHost: string;
    smtpPort: number;
    useSsl: boolean;
    smtpUsername: string;
    smtpPassword: string;
    fromEmail: string;
    fromName: string;
    isEnabled: boolean;
}

export const EmailSettings = () => {
    const [settings, setSettings] = useState<IEmailSettings>({
        smtpHost: '',
        smtpPort: 587,
        useSsl: true,
        smtpUsername: '',
        smtpPassword: '',
        fromEmail: '',
        fromName: 'SmartCashBack',
        isEnabled: true
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testingSending, setTestingSending] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const { toast, showError, showSuccess, handleClose } = useToast();

    const checkAdminRole = useCallback(async () => {
        const user = await authService.getUser();
        const roles = user?.profile?.role;
        if (roles && (Array.isArray(roles) ? roles.includes('Admin') : roles === 'Admin')) {
            setIsAdmin(true);
        }
    }, []);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch('api/emailsettings');
            if (response.ok) {
                const data = await response.json();
                setSettings(data);
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить настройки');
                throw new Error(errorMsg);
            }
        } catch (error: any) {
            showError(error.message || 'Ошибка загрузки настроек');
        } finally {
            setLoading(false);
        }
    }, [showError]);

    useEffect(() => {
        checkAdminRole();
        fetchSettings();
    }, [checkAdminRole, fetchSettings]);

    const handleChange = (field: keyof IEmailSettings) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = field === 'smtpPort' ? parseInt(event.target.value) :
                     field === 'useSsl' || field === 'isEnabled' ? event.target.checked :
                     event.target.value;
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch('api/emailsettings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });

            if (response.ok) {
                showSuccess('Настройки email успешно сохранены');
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось сохранить настройки');
                throw new Error(errorMsg);
            }
        } catch (error: any) {
            showError(error.message || 'Ошибка сохранения настроек');
        } finally {
            setSaving(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testEmail) {
            showError('Введите email адрес для тестовой отправки');
            return;
        }

        setTestingSending(true);
        try {
            const response = await fetch('api/emailsettings/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: testEmail })
            });

            if (response.ok) {
                showSuccess('Тестовое письмо успешно отправлено на ' + testEmail);
                setTestDialogOpen(false);
                setTestEmail('');
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось отправить тестовое письмо');
                throw new Error(errorMsg);
            }
        } catch (error: any) {
            showError(error.message || 'Ошибка отправки тестового письма');
        } finally {
            setTestingSending(false);
        }
    };

    if (!isAdmin) {
        return (
            <Card>
                <CardHeader title="Доступ запрещен" />
                <CardContent>
                    <Typography>У вас нет прав для просмотра этой страницы.</Typography>
                </CardContent>
            </Card>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <ToastNotification toast={toast} onClose={handleClose} />

            <Card>
                <CardHeader title="Настройки почтового сервера" />
                <CardContent>
                    <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={<Switch checked={settings.isEnabled} onChange={handleChange('isEnabled')} />}
                            label="Включить отправку email"
                        />

                        <TextField
                            label="SMTP хост"
                            value={settings.smtpHost}
                            onChange={handleChange('smtpHost')}
                            fullWidth
                            placeholder="smtp.gmail.com"
                            helperText="Адрес SMTP сервера"
                        />

                        <TextField
                            label="SMTP порт"
                            type="number"
                            value={settings.smtpPort}
                            onChange={handleChange('smtpPort')}
                            fullWidth
                            helperText="Обычно 587 для TLS или 465 для SSL"
                        />

                        <FormControlLabel
                            control={<Switch checked={settings.useSsl} onChange={handleChange('useSsl')} />}
                            label="Использовать SSL/TLS"
                        />

                        <TextField
                            label="SMTP логин"
                            value={settings.smtpUsername}
                            onChange={handleChange('smtpUsername')}
                            fullWidth
                            helperText="Имя пользователя для SMTP аутентификации"
                        />

                        <TextField
                            label="SMTP пароль"
                            type="password"
                            value={settings.smtpPassword}
                            onChange={handleChange('smtpPassword')}
                            fullWidth
                            helperText="Пароль для SMTP аутентификации"
                        />

                        <TextField
                            label="Email отправителя"
                            type="email"
                            value={settings.fromEmail}
                            onChange={handleChange('fromEmail')}
                            fullWidth
                            placeholder="noreply@example.com"
                            helperText="Адрес, с которого будут отправляться письма"
                        />

                        <TextField
                            label="Имя отправителя"
                            value={settings.fromName}
                            onChange={handleChange('fromName')}
                            fullWidth
                            helperText="Имя, отображаемое в поле 'От' в письмах"
                        />

                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? <CircularProgress size={24} /> : 'Сохранить'}
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => setTestDialogOpen(true)}
                                disabled={!settings.isEnabled}
                            >
                                Отправить тестовое письмо
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)}>
                <DialogTitle>Отправить тестовое письмо</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Email получателя"
                        type="email"
                        fullWidth
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestDialogOpen(false)}>Отмена</Button>
                    <Button onClick={handleTestEmail} disabled={testingSending} variant="contained">
                        {testingSending ? <CircularProgress size={24} /> : 'Отправить'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
